import { Component, inject, signal } from '@angular/core';
import { FormViewComponent } from '../form-view/form-view.component';
import {
  Address,
  ClaimRequest,
  ICaseCreateDto,
  ICaseExtension,
} from '../../../core/interfaces';
import { catchError, finalize, mergeMap, of, tap, throwError } from 'rxjs';

import { CaseService } from '../../../core/case.service';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  keyClientList,
  accomodationRequirementList,
  claimTypesList,
} from 'app/core/data';

type SubmitEvent =
  | {
      bookingType: 'newBooking';
      form: FormGroup;
      requestedLocation: Address;
      insuredAddress: Address;
    }
  | {
      bookingType: 'extension';
      form: FormGroup;
    };

@Component({
  selector: 'app-form',
  imports: [FormViewComponent],
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
})
export class FormComponent {
  private router = inject(Router);
  caseService = inject(CaseService);
  caseNumber = signal<string>('');
  submitSuccess = signal(false);
  submitError = signal<string | null>(null);

  //data for form
  keyClientList: string[] = keyClientList;
  accomodationRequirementList: string[] = accomodationRequirementList;
  claimTypesList: string[] = claimTypesList;

  isSubmittingInProgress = signal(false);

  submit(event: SubmitEvent) {
    this.isSubmittingInProgress.set(true);
    this.submitError.set(null);
    this.submitSuccess.set(false);
    const request$ =
      event.bookingType === 'newBooking'
        ? this.caseService.createCase(
            this.mapDtoToApiPayloadNewCase(
              event.form.getRawValue() as ICaseCreateDto,
              event.requestedLocation,
              event.insuredAddress
            )
          )
        : this.caseService.extensionCase(
            this.mapDtoToApiPayloadExtensionCase(
              event.form.getRawValue() as ICaseExtension
            )
          );

    request$
      .pipe(
        mergeMap((res: any) => {
          const rawMessage = res?.message;
          const message =
            typeof rawMessage === 'string' ? rawMessage.trim() : '';

          const isBusinessError =
            message &&
            (message.toLowerCase().includes('not found') ||
              message.toLowerCase().includes('error') ||
              message.toLowerCase().includes('failed'));

          if (isBusinessError) {
            return throwError(() => ({
              kind: 'business',
              message,
              raw: res,
            }));
          }

          return of(res);
        }),

        tap((res: any) => {
          const message = String(res?.message ?? 'Success').trim();
          const caseId = res?.caseId ?? this.caseNumber();

          if (!caseId) {
            console.warn('No caseId and no existing caseNumber', res);
            return;
          }

          this.caseNumber.set(caseId);
          this.submitSuccess.set(true);
          this.submitError.set(null);

          this.router
            .navigate(['/case', caseId], {
              state: { success: true, message },
            })
            .then(() => window.scrollTo(0, 0));
        }),

        catchError((error) => {
          const errorMessage =
            error?.message ||
            error?.error?.message ||
            'Request failed. Please try again.';

          console.error('Submit failed:', errorMessage);

          this.submitError.set(errorMessage);
          this.submitSuccess.set(false);

          return of(null);
        }),

        finalize(() => this.isSubmittingInProgress.set(false))
      )
      .subscribe();
  }

  mapDtoToApiPayloadNewCase(
    dto: ICaseCreateDto,
    requestedLocation: Address,
    insuredAddress: Address
  ): ClaimRequest {
    return {
      Guest: {
        FirstName: dto.policyHolderFirstName,
        Surname: dto.policyHolderSurname,
        DateOfBirth: this.formatDateToApi(dto.policyHolderDateOfBirth),
        Total10Limit: dto.totalTenPercentLimit,
        NightlyBudget: dto.nightlyBudget,
        Email: dto.policyHolderEmail,
        PhoneNo: dto.policyHolderPhone,
        AltPhoneNo: dto.policyHolderAltPhone,
        ConsentSMS: dto.consentSMS ? 1 : 0,
        ConsentEmail: dto.consentEmail ? 1 : 0,
      },
      InsuredAddress: insuredAddress,
      RequestedLocation: requestedLocation,
      Requirements: {
        CheckInDate: this.formatDateToApi(dto.checkInDate),
        CheckOutDate: this.formatDateToApi(dto.checkOutDate),
        AdultsQty: dto.noAdults,
        ChildQty: dto.grownUpChildren,
        InfantQty: dto.noChildrenSmall,
        CarParkQty: dto.noCarParks,
        VehicleRegos: dto.vehicleRegos,
        DogQty: dto.dogs,
        DogBreeds: dto.dogBreeds,
        CatQty: dto.cats,
        CatBreeds: dto.catBreeds,
        MobilityIssues: dto.mobilityIssues,
        Note: dto.additionalNotes,
        RequirementsId: '',
        PublicTransport: '',
        AccommodationProvider: '',
      },
      Priority: 0,
      IncidentType: dto.incidentType,
      IncidentDate: this.formatDateToApi(dto.incidentDate),

      Consultant: {
        FirstName: dto.claimConsultantFirstName,
        Surname: dto.claimConsultantSurname,
        Email: 'home@autogeneral.com.au',
        AdditionalNotes: dto.claimConsultantAdditionalNotes,
        Ext: dto.claimConsultantExt?.toString() ?? '',
        KeyClientId: dto.keyClient,
        ConsultantId: '',
      },
      InitialImpact: dto.claimType,
      Status: 'New Request',
      ClaimNumber: dto.claimNumber,
    };
  }

  mapDtoToApiPayloadExtensionCase(dto: ICaseExtension) {
    this.caseNumber.set(dto.caseNumber);
    return {
      CaseNumber: dto.caseNumber,
      ClaimNumber: dto.claimNumber,
      CheckInDate: this.formatDateToApi(dto.checkInDate),
      CheckOutDate: this.formatDateToApi(dto.checkOutDate),
      RequestingConsultant: dto.requestingConsultant,
    };
  }

  formatDateToApi(value: string | Date | null | undefined): string {
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
